import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CountryData, defaultCountries, parseCountry } from 'react-international-phone';
import { RootState } from 'store';
import {
    FALLBACK_COUNTRY_ISO,
    fetchAllowedCountries,
    getDefaultCountryIso,
    resolveDefaultCountry,
} from 'services/api/country';

const indiaOnlyCountries = defaultCountries.filter(
    (country) => parseCountry(country).iso2 === 'in'
);

export const useAuthPhoneCountries = () => {
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const [allowedIso, setAllowedIso] = useState<string[]>([FALLBACK_COUNTRY_ISO]);
    const [preferredIso, setPreferredIso] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const loadCountries = async () => {
            try {
                const [codes, iso] = await Promise.all([
                    fetchAllowedCountries().catch((error) => {
                        console.warn('Failed to load allowed countries', error);
                        return [] as string[];
                    }),
                    getDefaultCountryIso(isAuthenticated).catch((error) => {
                        console.warn('Failed to resolve default country', error);
                        return null;
                    }),
                ]);

                if (cancelled) return;
                if (codes.length > 0) setAllowedIso(codes);
                setPreferredIso(iso);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadCountries();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    const countries = useMemo<CountryData[]>(() => {
        const allowed = new Set(allowedIso.map((code) => code.toLowerCase()));
        const filtered = defaultCountries.filter((country) =>
            allowed.has(parseCountry(country).iso2)
        );
        return filtered.length ? filtered : indiaOnlyCountries;
    }, [allowedIso]);

    const defaultCountry = resolveDefaultCountry(allowedIso, preferredIso).toLowerCase();

    return { countries, defaultCountry, loading };
};
